import { useState, useEffect } from 'react';
import { Cookie, X, Settings } from 'lucide-react';
import { 
  hasAcceptedCookies, 
  acceptCookies, 
  declineCookies,
  getCookiePreferences,
  saveCookiePreferences 
} from '../utils/cookies';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const hasConsent = hasAcceptedCookies();
    if (!hasConsent) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 1000);
    }

    // Load existing preferences
    const savedPrefs = getCookiePreferences();
    setPreferences(savedPrefs);
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    saveCookiePreferences(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleDecline = () => {
    declineCookies();
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    saveCookiePreferences(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleTogglePreference = (key) => {
    if (key === 'necessary') return; // Necessary cookies can't be disabled
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop */}
      {showSettings && (
        <div 
          className="fixed inset-0 bg-black/50 z-[9998] animate-fade-in"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6 animate-slide-up">
        <div className="max-w-6xl mx-auto bg-gradient-to-r from-slate-900 via-slate-800 to-amber-900 rounded-2xl shadow-2xl border border-amber-500/30 overflow-hidden">
          <div className="p-6 sm:p-8">
            {!showSettings ? (
              // Main Banner
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                    <Cookie className="w-6 h-6 text-amber-400" />
                  </div>
                </div>

                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">
                    We use cookies
                  </h3>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                    By clicking "Accept All", you consent to our use of cookies.{' '}
                    <a href="/privacy-policy" className="text-amber-400 hover:text-amber-300 underline">
                      Learn more
                    </a>
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => setShowSettings(true)}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 transition-colors text-sm font-medium"
                  >
                    <Settings className="w-4 h-4" />
                    Preferences
                  </button>
                  
                  <button
                    onClick={handleDecline}
                    className="px-6 py-3 rounded-lg border border-slate-500 text-slate-300 hover:bg-slate-700/50 transition-colors text-sm font-medium"
                  >
                    Decline
                  </button>
                  
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 hover:from-amber-600 hover:to-amber-700 transition-all text-sm font-bold shadow-lg"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            ) : (
              // Settings Panel
              <div className="relative">
                <button
                  onClick={() => setShowSettings(false)}
                  className="absolute top-0 right-0 text-slate-400 hover:text-white transition-colors"
                  aria-label="Close settings"
                >
                  <X className="w-5 h-5" />
                </button>

                <h3 className="text-xl font-bold text-white mb-6">
                  Cookie Preferences
                </h3>

                <div className="space-y-4 mb-6">
                  {/* Necessary Cookies */}
                  <div className="flex items-start justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                    <div className="flex-1 pr-4">
                      <h4 className="font-semibold text-white mb-1">
                        Necessary Cookies
                      </h4>
                      <p className="text-sm text-slate-400">
                        Essential for the website to function properly. Cannot be disabled.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-12 h-6 bg-amber-500 rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-start justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                    <div className="flex-1 pr-4">
                      <h4 className="font-semibold text-white mb-1">
                        Analytics Cookies
                      </h4>
                      <p className="text-sm text-slate-400">
                        Help us understand how visitors interact with our website.
                      </p>
                    </div>
                    <button
                      onClick={() => handleTogglePreference('analytics')}
                      className="flex-shrink-0"
                      aria-label="Toggle analytics cookies"
                    >
                      <div className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                        preferences.analytics 
                          ? 'bg-amber-500 justify-end' 
                          : 'bg-slate-600 justify-start'
                      } px-1`}>
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </button>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="flex items-start justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                    <div className="flex-1 pr-4">
                      <h4 className="font-semibold text-white mb-1">
                        Marketing Cookies
                      </h4>
                      <p className="text-sm text-slate-400">
                        Used to track visitors across websites for marketing purposes.
                      </p>
                    </div>
                    <button
                      onClick={() => handleTogglePreference('marketing')}
                      className="flex-shrink-0"
                      aria-label="Toggle marketing cookies"
                    >
                      <div className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                        preferences.marketing 
                          ? 'bg-amber-500 justify-end' 
                          : 'bg-slate-600 justify-start'
                      } px-1`}>
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </button>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleSavePreferences}
                    className="flex-1 px-6 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 hover:from-amber-600 hover:to-amber-700 transition-all text-sm font-bold"
                  >
                    Save Preferences
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-3 rounded-lg border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 transition-colors text-sm font-medium"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
